'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import { BookOpen, Mic, FileText, Plus, Trash2, Save, Send, AlertTriangle, ChevronDown, ChevronUp, Info, RefreshCw, X, Image, Video, Music, ChevronRight, FolderOpen, Archive, Layers, Check } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import { submitContribution, getUserDocProjects, linkStoryToProject, updateContributionMetadata } from '@/lib/db/contributions';
import { submitContributionToArchive } from '@/lib/db/archives';
import { trackEvent } from '@/lib/analytics';
import { saveDraftToSupabase, loadDraftFromSupabase, deleteDraftFromSupabase } from '@/lib/db/drafts';
import CitationAuthenticator from './CitationAuthenticator';
import ArchiveSelector from './ArchiveSelector';
import { useLanguage } from '@/contexts/LanguageContext';
import { SUPPORTED_LANGUAGES, getTranslations } from '@/lib/i18n';
import Icon from '@/components/ui/AppIcon';

// Media uploads available post-submission via contribution detail page

const RichTextEditor = dynamic(() => import('./RichTextEditor'), { ssr: false });

interface Citation {
  id: string;
  title: string;
  author: string;
  publicationDate: string;
  url: string;
  archiveSource: string;
  documentType: string;
  verificationStatus: 'unverified' | 'verified' | 'broken';
}

interface FormData {
  title: string;
  era: string;
  topic: string;
  region: string;
  certaintyLabel: 'oral_tradition' | 'sourced_claim' | 'firsthand_account' | '';
  isAnonymous: boolean;
  body: string;
}

const eras = [
  { id: 'era-1', label: 'Early Iron Age (200–900 CE)', slug: 'early-iron-age' },
  { id: 'era-2', label: 'Kingdom of Mutapa (900–1700 CE)', slug: 'mutapa-kingdom' },
  { id: 'era-3', label: 'Rozvi Empire (1680–1866 CE)', slug: 'rozvi-empire' },
  { id: 'era-4', label: 'Colonial Contact (1850–1900 CE)', slug: 'colonial-contact' },
  { id: 'era-5', label: 'Anti Colonial Resistance (1890–1920 CE)', slug: 'colonial-resistance' },
  { id: 'era-6', label: 'Nationalist Period (1950–1980 CE)', slug: 'nationalist-period' },
  { id: 'era-7', label: 'Post-Independence (1980–present)', slug: 'post-independence' },
];

const topicsByEra: Record<string, { id: string; label: string }[]> = {
  'era-1': [
    { id: 'topic-101', label: 'Early Iron Age Settlements' },
    { id: 'topic-102', label: 'Stone Building Traditions' },
    { id: 'topic-103', label: 'Pre-agricultural Communities' },
  ],
  'era-2': [
    { id: 'topic-201', label: 'Mutapa Kingdom Politics' },
    { id: 'topic-202', label: 'Gold Trade Routes' },
    { id: 'topic-203', label: 'Portuguese Contact' },
    { id: 'topic-204', label: 'Oral Law Traditions' },
  ],
  'era-3': [
    { id: 'topic-301', label: 'Rozvi Empire' },
    { id: 'topic-302', label: 'Great Zimbabwe (Rozvi Period)' },
    { id: 'topic-303', label: 'Ndebele State Formation' },
  ],
  'era-4': [
    { id: 'topic-401', label: 'Missionary Activity' },
    { id: 'topic-402', label: 'Early Colonial Administration' },
    { id: 'topic-403', label: 'Pioneer Column' },
  ],
  'era-5': [
    { id: 'topic-501', label: 'Chimurenga Wars' },
    { id: 'topic-502', label: 'Matabeleland Uprisings' },
    { id: 'topic-503', label: 'Spirit Medium Resistance' },
    { id: 'topic-504', label: 'Colonial Land Acts' },
  ],
  'era-6': [
    { id: 'topic-601', label: 'Liberation Struggle (1960s)' },
    { id: 'topic-602', label: 'ZAPU / ZANU Formation' },
    { id: 'topic-603', label: 'Urban Nationalism' },
    { id: 'topic-604', label: 'UDI Period' },
  ],
  'era-7': [
    { id: 'topic-701', label: 'Independence 1980' },
    { id: 'topic-702', label: 'Gukurahundi' },
    { id: 'topic-703', label: 'Land Reform' },
    { id: 'topic-704', label: 'Economic Crisis' },
  ],
};

const certaintyOptions = [
  {
    id: 'oral_tradition',
    label: 'Oral Tradition',
    desc: 'Knowledge passed down through generations by word of mouth. No written source exists or is required.',
    badge: 'oral' as const,
    icon: Mic,
  },
  {
    id: 'sourced_claim',
    label: 'Sourced Claim',
    desc: 'Based on a written source, archive document, or published record. At least one citation required.',
    badge: 'sourced' as const,
    icon: BookOpen,
  },
  {
    id: 'firsthand_account',
    label: 'Firsthand Account',
    desc: 'A direct personal experience or testimony from someone who witnessed the event.',
    badge: 'firsthand' as const,
    icon: FileText,
  },
];

const regions = [
  'Mashonaland East', 'Mashonaland West', 'Mashonaland Central',
  'Matabeleland North', 'Matabeleland South',
  'Manicaland', 'Midlands', 'Masvingo',
  'Harare (Metropolitan)', 'Bulawayo (Metropolitan)',
  'Pan-African / Diaspora', 'Other',
];

const documentTypes = ['Book', 'Journal Article', 'Newspaper', 'Archive Document', 'Government Record', 'Photograph', 'Audio Recording', 'Website', 'Other'];

function emptyCitation(index: number): Citation {
  return {
    id: `citation-new-${index}`,
    title: '',
    author: '',
    publicationDate: '',
    url: '',
    archiveSource: '',
    documentType: 'Book',
    verificationStatus: 'unverified',
  };
}

// ─── Workflow Chain Banner ────────────────────────────────────────────────────

interface WorkflowStep {
  icon: React.ElementType;
  label: string;
  sublabel: string;
  active: boolean;
  color: string;
}

function WorkflowChainBanner({ projectSelected, archiveSelected }: { projectSelected: boolean; archiveSelected: boolean }) {
  const steps: WorkflowStep[] = [
    {
      icon: FileText,
      label: 'Story',
      sublabel: 'Write & submit',
      active: true,
      color: 'var(--primary)',
    },
    {
      icon: FolderOpen,
      label: 'Project',
      sublabel: projectSelected ? 'Linked ✓' : 'Link below',
      active: projectSelected,
      color: '#2C5E3B',
    },
    {
      icon: Archive,
      label: 'Archive',
      sublabel: archiveSelected ? 'Linked ✓' : 'Select below',
      active: archiveSelected,
      color: '#C17A3E',
    },
    {
      icon: Layers,
      label: 'The Wall',
      sublabel: archiveSelected ? 'Eligible' : 'After archive',
      active: archiveSelected,
      color: '#1D4ED8',
    },
  ];

  return (
    <div
      className="rounded-xl border p-4 mb-6"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
    >
      <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>
        Submission Workflow
      </p>
      <div className="flex items-center gap-1 flex-wrap">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          return (
            <React.Fragment key={step.label}>
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium"
                style={{
                  backgroundColor: step.active ? `${step.color}10` : 'var(--muted)',
                  borderColor: step.active ? `${step.color}40` : 'var(--border)',
                  color: step.active ? step.color : 'var(--muted-foreground)',
                }}
              >
                <Icon size={13} />
                <div>
                  <div className="font-semibold leading-none">{step.label}</div>
                  <div className="text-xs opacity-75 mt-0.5 leading-none">{step.sublabel}</div>
                </div>
                {step.active && step.label !== 'Story' && (
                  <span
                    className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: step.color }}
                  >
                    <Check size={9} color="white" />
                  </span>
                )}
              </div>
              {idx < steps.length - 1 && (
                <ChevronRight size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <p className="text-xs mt-3" style={{ color: 'var(--muted-foreground)' }}>
        A story linked to a project and archive becomes eligible to appear on{' '}
        <strong style={{ color: 'var(--foreground)' }}>The Wall</strong> — the living mosaic of Griott history.
      </p>
    </div>
  );
}

export default function SubmissionForm() {
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: { certaintyLabel: '', isAnonymous: false },
  });

  const searchParams = useSearchParams();

  // ── Edit mode ─────────────────────────────────────────────────────────────
  const editId = searchParams?.get('edit') || null;
  const [isEditMode, setIsEditMode] = useState(!!editId);
  const [editLoading, setEditLoading] = useState(!!editId);
  const [editLoadError, setEditLoadError] = useState<string | null>(null);
  const [editInitialBody, setEditInitialBody] = useState<string | undefined>(undefined);

  const [body, setBody] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [citations, setCitations] = useState<Citation[]>([]);
  const [draftSaved, setDraftSaved] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [citationsExpanded, setCitationsExpanded] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedArchiveId, setSelectedArchiveId] = useState<string | null>(null);
  const [isNewArchive, setIsNewArchive] = useState(false);
  const [newArchiveTitle, setNewArchiveTitle] = useState('');
  const [storyLanguage, setStoryLanguage] = useState<string>('en');

  // Project linking state
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [userProjects, setUserProjects] = useState<{ id: string; title: string; category: string }[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Custom topic state
  const [isCustomTopic, setIsCustomTopic] = useState(false);
  const [customTopicLabel, setCustomTopicLabel] = useState('');

  // Featured image state
  const [featuredImageUrl, setFeaturedImageUrl] = useState<string>('');
  const [featuredImageUploading, setFeaturedImageUploading] = useState(false);
  const [featuredImagePreview, setFeaturedImagePreview] = useState<string>('');
  const featuredImageRef = React.useRef<HTMLInputElement>(null);

  // Direct audio upload state
  const [audioUploadFile, setAudioUploadFile] = useState<File | null>(null);
  const [audioUploading, setAudioUploading] = useState(false);
  const [audioUploadError, setAudioUploadError] = useState<string | null>(null);
  const audioUploadRef = React.useRef<HTMLInputElement>(null);

  // Draft recovery state
  const [recoveredDraft, setRecoveredDraft] = useState<{ lastSavedAt: string } | null>(null);
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [draftLoading, setDraftLoading] = useState(true);
  const [draftRestored, setDraftRestored] = useState(false);

  // Pre-fill from voice recording transcript
  const [voiceInitialContent, setVoiceInitialContent] = useState<string | undefined>(undefined);

  // Audio metadata from oral recording
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioDurationSeconds, setAudioDurationSeconds] = useState<number | undefined>(undefined);
  const [audioFileSizeBytes, setAudioFileSizeBytes] = useState<number | undefined>(undefined);
  const [audioUploadedAt, setAudioUploadedAt] = useState<string | undefined>(undefined);
  const [isFromRecording, setIsFromRecording] = useState(false);

  // Submission type: 'text' | 'audio_text'
  const [submissionType, setSubmissionType] = useState<'text' | 'audio_text'>('text');

  const { language } = useLanguage();
  const t = getTranslations(language);

  const selectedEra = watch('era');
  const certaintyLabel = watch('certaintyLabel');
  const isAnonymous = watch('isAnonymous');
  const watchedTitle = watch('title');

  const availableTopics = selectedEra ? (topicsByEra[selectedEra] || []) : [];

  // On mount: check for a saved Supabase draft
  useEffect(() => {
    // Skip draft loading when in edit mode
    if (editId) {
      setDraftLoading(false);
      return;
    }
    let cancelled = false;
    loadDraftFromSupabase()
      .then((draft) => {
        if (cancelled) return;
        if (draft && (draft.title || draft.body)) {
          // ── Fix 8: Stale draft auto-discard after 7 days ──────────────────
          const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
          const lastSaved = draft.lastSavedAt ? new Date(draft.lastSavedAt).getTime() : 0;
          if (lastSaved && Date.now() - lastSaved > SEVEN_DAYS_MS) {
            // Silently discard stale draft
            deleteDraftFromSupabase().catch(() => {});
            return;
          }
          setRecoveredDraft({ lastSavedAt: draft.lastSavedAt });
          setShowDraftBanner(true);
          trackEvent('story_funnel_draft_recovery_offered', {
            draft_last_saved: draft.lastSavedAt,
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setDraftLoading(false);
      });
    return () => { cancelled = true; };
  }, [editId]);

  // Load existing contribution when in edit mode
  useEffect(() => {
    if (!editId) return;
    setEditLoadError(null);

    const loadExisting = async () => {
      try {
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();

        // ── Fix 1: Permission check ────────────────────────────────────────
        const { data: { user } } = await supabase.auth.getUser();

        const { data, error } = await supabase
          .from('contributions')
          .select('id, title, body, certainty_label, era_id, topic_id, region, is_anonymous, author_id')
          .eq('id', editId)
          .single();

        if (error || !data) {
          setEditLoadError('Could not load the story. It may have been removed or you may not have permission to edit it.');
          setEditLoading(false);
          return;
        }

        // Check ownership or curator/admin role
        if (user) {
          const isOwner = data.author_id === user.id;
          if (!isOwner) {
            // Check if user has curator or higher role
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('role')
              .eq('id', user.id)
              .single();
            const elevatedRoles = ['curator', 'historian', 'admin', 'super_admin'];
            const hasElevatedRole = profile?.role && elevatedRoles.includes(profile.role);
            if (!hasElevatedRole) {
              setEditLoadError('You do not have permission to edit this story. Only the original author or a curator can edit it.');
              setEditLoading(false);
              return;
            }
          }
        } else {
          setEditLoadError('You must be signed in to edit a story.');
          setEditLoading(false);
          return;
        }

        // Pre-fill form fields
        setValue('title', data.title || '');
        if (data.era_id) setValue('era', data.era_id);
        if (data.topic_id) setValue('topic', data.topic_id);
        if (data.region) setValue('region', data.region);
        if (data.certainty_label) setValue('certaintyLabel', data.certainty_label as FormData['certaintyLabel']);
        setValue('isAnonymous', data.is_anonymous || false);

        // Pre-fill body for RichTextEditor
        if (data.body) {
          setBody(data.body);
          setEditInitialBody(data.body);
        }
      } catch (err: any) {
        setEditLoadError('Failed to load story for editing. Please try again.');
      } finally {
        setEditLoading(false);
      }
    };

    loadExisting();
  }, [editId, setValue]);

  // Load user's doc projects for linking
  useEffect(() => {
    setLoadingProjects(true);
    getUserDocProjects()
      .then((projects) => setUserProjects(projects))
      .catch(() => {})
      .finally(() => setLoadingProjects(false));
  }, []);

  useEffect(() => {
    const transcript = searchParams?.get('voice_transcript');
    const certainty = searchParams?.get('certainty');
    const audioUrlParam = searchParams?.get('audio_url');
    const audioDurationParam = searchParams?.get('audio_duration');
    const audioFileSizeParam = searchParams?.get('audio_file_size');
    const audioUploadedAtParam = searchParams?.get('audio_uploaded_at');

    if (transcript) {
      setBody(transcript);
      setVoiceInitialContent(transcript);
    }
    if (certainty && ['oral_tradition', 'sourced_claim', 'firsthand_account'].includes(certainty)) {
      setValue('certaintyLabel', certainty as FormData['certaintyLabel']);
    }
    if (audioUrlParam) {
      setAudioUrl(audioUrlParam);
      setIsFromRecording(true);
      setSubmissionType('audio_text');
    }
    if (audioDurationParam) {
      const dur = parseInt(audioDurationParam, 10);
      if (!isNaN(dur)) setAudioDurationSeconds(dur);
    }
    if (audioFileSizeParam) {
      const size = parseInt(audioFileSizeParam, 10);
      if (!isNaN(size)) setAudioFileSizeBytes(size);
    }
    if (audioUploadedAtParam) {
      setAudioUploadedAt(audioUploadedAtParam);
    }
  }, [searchParams, setValue]);

  const handleRestoreDraft = useCallback(async () => {
    const draft = await loadDraftFromSupabase();
    if (!draft) return;
    if (draft.title) setValue('title', draft.title);
    if (draft.eraId) setValue('era', draft.eraId);
    if (draft.topicId) setValue('topic', draft.topicId);
    if (draft.region) setValue('region', draft.region);
    if (draft.certaintyLabel) setValue('certaintyLabel', draft.certaintyLabel as any);
    setValue('isAnonymous', draft.isAnonymous);
    if (draft.citations?.length) setCitations(draft.citations);
    if (draft.selectedArchiveId) setSelectedArchiveId(draft.selectedArchiveId);
    setIsNewArchive(draft.isNewArchive);
    if (draft.newArchiveTitle) setNewArchiveTitle(draft.newArchiveTitle);
    // body is set via RichTextEditor's initialValue — store it for now
    setBody(draft.body || '');
    setDraftRestored(true);
    setShowDraftBanner(false);
    setIsCustomTopic(false);
    setCustomTopicLabel('');
    trackEvent('story_funnel_draft_restored', {
      draft_last_saved: draft.lastSavedAt,
    });
  }, [setValue]);

  const handleDiscardDraft = useCallback(async () => {
    await deleteDraftFromSupabase();
    setShowDraftBanner(false);
    setRecoveredDraft(null);
    trackEvent('story_funnel_draft_discarded', {});
  }, []);

  const handleBodyChange = useCallback((html: string, text: string) => {
    setBody(html);
    const words = text.trim().split(/\s+/).filter(Boolean);
    setWordCount(words.length);
  }, []);

  const saveDraft = useCallback(async () => {
    setSavingDraft(true);
    const formValues = {
      title: watchedTitle || '',
      body,
      eraId: selectedEra || '',
      topicId: watch('topic') || '',
      region: watch('region') || '',
      certaintyLabel: certaintyLabel || '',
      isAnonymous: isAnonymous || false,
      citations,
      selectedArchiveId,
      isNewArchive,
      newArchiveTitle,
    };

    // Try Supabase first; fall back to localStorage for unauthenticated users
    const savedId = await saveDraftToSupabase(formValues);
    if (!savedId && typeof window !== 'undefined') {
      localStorage.setItem('griott_draft', JSON.stringify(formValues));
    }

    setSavingDraft(false);
    setDraftSaved(true);
    setTimeout(() => setDraftSaved(false), 3000);
  }, [body, citations, watchedTitle, selectedEra, certaintyLabel, isAnonymous, selectedArchiveId, isNewArchive, newArchiveTitle, watch]);

  // Auto-save every 5 seconds when body has meaningful content
  useEffect(() => {
    // ── Fix 4: Disable auto-save in edit mode ─────────────────────────────
    if (isEditMode) return;
    const timer = setTimeout(() => {
      if (body.length > 50) saveDraft();
    }, 5000);
    return () => clearTimeout(timer);
  }, [body, saveDraft, isEditMode]);

  // GA funnel: track when user starts filling the form
  useEffect(() => {
    if (watchedTitle && watchedTitle.length > 5) {
      trackEvent('story_funnel_title_entered', { title_length: watchedTitle.length });
    }
  }, [watchedTitle]);

  // GA funnel: track era selection
  useEffect(() => {
    if (selectedEra) {
      trackEvent('story_funnel_era_selected', { era: selectedEra });
    }
  }, [selectedEra]);

  // GA funnel: track certainty label selection
  useEffect(() => {
    if (certaintyLabel) {
      trackEvent('story_funnel_certainty_selected', { certainty_label: certaintyLabel });
    }
  }, [certaintyLabel]);

  const addCitation = () => {
    setCitations((prev) => [...prev, emptyCitation(prev.length)]);
    trackEvent('story_funnel_citation_added', { citation_count: citations.length + 1 });
  };

  const removeCitation = (id: string) => {
    setCitations((prev) => prev.filter((c) => c.id !== id));
  };

  const updateCitation = (id: string, field: keyof Citation, value: string) => {
    setCitations((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  };

  const handleApplySuggestedCitation = useCallback((suggestion: { title: string; author: string; url: string; documentType: string }) => {
    const newCitation: Citation = {
      id: `citation-ai-${Date.now()}`,
      title: suggestion.title,
      author: suggestion.author,
      publicationDate: '',
      url: suggestion.url,
      archiveSource: '',
      documentType: suggestion.documentType || 'Website',
      verificationStatus: 'verified',
    };
    setCitations((prev) => [...prev, newCitation]);
    setCitationsExpanded(true);
    trackEvent('story_funnel_ai_citation_applied', { citation_title: suggestion.title });
  }, []);

  const handleArchiveSelected = useCallback((archiveId: string | null, isNew: boolean, newTitle?: string) => {
    setSelectedArchiveId(archiveId);
    setIsNewArchive(isNew);
    setNewArchiveTitle(newTitle || '');
    if (archiveId || isNew) {
      trackEvent('story_funnel_archive_selected', {
        archive_id: archiveId || 'new',
        is_new_archive: isNew,
      });
    }
  }, []);

  const handleFeaturedImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFeaturedImageUploading(true);
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const ext = file.name.split('.').pop();
      const path = `featured/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { data, error } = await supabase.storage.from('contribution-media').upload(path, file, { upsert: false });
      if (!error && data) {
        const { data: urlData } = supabase.storage.from('contribution-media').getPublicUrl(data.path);
        setFeaturedImageUrl(urlData.publicUrl);
        setFeaturedImagePreview(urlData.publicUrl);
      }
    } catch {
      // silently fail
    } finally {
      setFeaturedImageUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleAudioUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAudioUploadError(null);
    const maxSize = 100 * 1024 * 1024; // 100 MB
    if (file.size > maxSize) {
      setAudioUploadError('File too large. Maximum size is 100 MB.');
      if (e.target) e.target.value = '';
      return;
    }

    // Normalize audio/webm to audio/ogg so Supabase storage accepts it.
    // Chrome records as audio/webm which may be blocked by the bucket's MIME filter.
    let uploadFile: File = file;
    if (file.type.startsWith('audio/webm') || file.name.toLowerCase().endsWith('.webm')) {
      const baseName = file.name.replace(/\.[^.]+$/, '');
      uploadFile = new File([file], `${baseName}.ogg`, { type: 'audio/ogg' });
    }

    setAudioUploadFile(uploadFile);
    setAudioUploading(true);
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const ext = uploadFile.name.split('.').pop() || 'mp3';
      const path = `audio/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { data, error } = await supabase.storage.from('contribution-media').upload(path, uploadFile, { upsert: false });
      if (error) throw new Error(error.message);
      if (data) {
        const { data: urlData } = supabase.storage.from('contribution-media').getPublicUrl(data.path);
        setAudioUrl(urlData.publicUrl);
        setAudioUploadedAt(new Date().toISOString());
        setAudioFileSizeBytes(file.size);
        // Read duration from audio element
        const objectUrl = URL.createObjectURL(file);
        const audio = new window.Audio(objectUrl);
        audio.addEventListener('loadedmetadata', () => {
          if (isFinite(audio.duration)) {
            setAudioDurationSeconds(Math.round(audio.duration));
          }
          URL.revokeObjectURL(objectUrl);
        });
      }
    } catch (err: any) {
      setAudioUploadError(err.message || 'Upload failed. Please try again.');
      setAudioUploadFile(null);
    } finally {
      setAudioUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleRemoveAudio = () => {
    setAudioUrl(null);
    setAudioUploadFile(null);
    setAudioDurationSeconds(undefined);
    setAudioFileSizeBytes(undefined);
    setAudioUploadedAt(undefined);
    setAudioUploadError(null);
    setIsFromRecording(false);
  };

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);

    // Validate custom topic
    if (isCustomTopic && !customTopicLabel.trim()) {
      setSubmitError('Please enter a topic name.');
      setSubmitting(false);
      return;
    }

    // ── Fix 5: sourced_claim requires at least one citation ────────────────
    if (data.certaintyLabel === 'sourced_claim' && citations.length === 0) {
      setSubmitError('A "Sourced Claim" requires at least one citation. Please add a citation before submitting.');
      setSubmitting(false);
      return;
    }

    // ── Edit mode: update existing contribution ────────────────────────────
    if (isEditMode && editId) {
      try {
        const success = await updateContributionMetadata(editId, {
          title: data.title,
          body,
          eraId: data.era,
          topicId: isCustomTopic ? undefined : (data.topic || undefined),
          region: data.region || undefined,
          certaintyLabel: data.certaintyLabel || undefined,
        });

        if (success) {
          setSubmitted(true);
          trackEvent('story_funnel_edited', {
            contribution_id: editId,
            era: data.era,
            certainty_label: data.certaintyLabel,
          });
        } else {
          setSubmitError('Update failed. Please try again.');
        }
      } catch (err: any) {
        setSubmitError(err.message || 'Update failed. Please try again.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // GA funnel: submission attempted
    trackEvent('story_funnel_submit_attempted', {
      era: data.era,
      topic: data.topic,
      certainty_label: data.certaintyLabel,
      is_anonymous: data.isAnonymous,
      word_count: wordCount,
      citation_count: citations.length,
      archive_id: selectedArchiveId || undefined,
    });
    try {
      const wordText = body.replace(/<[^>]+>/g, ' ');
      const words = wordText.trim().split(/\s+/).filter(Boolean);
      const result = await submitContribution(
        {
          title: data.title,
          body,
          eraId: data.era,
          topicId: isCustomTopic ? undefined : data.topic,
          customTopicLabel: isCustomTopic ? customTopicLabel.trim() : undefined,
          region: data.region,
          certaintyLabel: data.certaintyLabel as 'oral_tradition' | 'sourced_claim' | 'firsthand_account',
          isAnonymous: data.isAnonymous,
          wordCount: words.length,
          excerpt: wordText.substring(0, 250),
          featuredImageUrl: featuredImageUrl || undefined,
          audioUrl: audioUrl || undefined,
          audioDurationSeconds,
          audioFileSizeBytes,
          audioUploadedAt,
        },
        citations.map((c) => ({
          title: c.title,
          author: c.author,
          publicationDate: c.publicationDate,
          url: c.url,
          archiveSource: c.archiveSource,
          documentType: c.documentType,
          verificationStatus: c.verificationStatus,
        }))
      );
      if (result) {
        // If contributor selected an existing archive, link the story to it
        if (selectedArchiveId && !isNewArchive) {
          await submitContributionToArchive(selectedArchiveId, result.id);
        }

        // If contributor selected a project, link the story to it
        if (selectedProjectId) {
          await linkStoryToProject(result.id, selectedProjectId);
        }

        setSubmitted(true);
        // GA funnel: submission completed
        trackEvent('story_funnel_submitted', {
          era: data.era,
          topic: data.topic,
          certainty_label: data.certaintyLabel,
          is_anonymous: data.isAnonymous,
          word_count: words.length,
          citation_count: citations.length,
          archive_id: selectedArchiveId || undefined,
          contribution_id: result.id,
        });

        // Delete Supabase draft after successful submission
        await deleteDraftFromSupabase();
        // Also clear localStorage fallback
        if (typeof window !== 'undefined') {
          localStorage.removeItem('griott_draft');
        }
      }
    } catch (err: any) {
      setSubmitError(err.message || 'Submission failed. Please try again.');
      // GA funnel: submission failed
      trackEvent('story_funnel_submit_failed', {
        error: err.message || 'unknown',
      });
      console.error('Submission failed:', err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted && isEditMode) {
    return (
      <div className="max-w-2xl mx-auto text-center py-24">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
          style={{ backgroundColor: '#2C5E3B18', color: '#2C5E3B' }}
        >
          <Check size={28} />
        </div>
        <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--foreground)' }}>
          Story updated
        </h2>
        <p className="text-sm mb-8" style={{ color: 'var(--muted-foreground)' }}>
          Your changes have been saved. The story will reflect your updates once the curator review is complete.
        </p>
        <a
          href={`/contributions/${editId}`}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-150 active:scale-95"
          style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
        >
          Back to Story
        </a>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto text-center py-24">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
          style={{ backgroundColor: '#2C5E3B18', color: '#2C5E3B' }}
        >
          <Send size={28} />
        </div>
        <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--foreground)' }}>
          Contribution submitted
        </h2>
        <p className="text-sm mb-4" style={{ color: 'var(--muted-foreground)' }}>
          Your entry is now in the curator queue. A human curator will review it — this typically takes 3–7 days.
          You&apos;ll receive a notification when a decision is made.
        </p>

        {/* Workflow status */}
        <div
          className="rounded-xl border p-4 text-left mb-6"
          style={{ borderColor: '#2C5E3B44', backgroundColor: '#2C5E3B0A' }}
        >
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#2C5E3B' }}>
            Your story&apos;s journey
          </p>
          <div className="flex items-center gap-1 flex-wrap">
            {[
              { label: 'Story', sublabel: 'Submitted ✓', color: 'var(--primary)', done: true },
              { label: 'Project', sublabel: selectedProjectId ? 'Linked ✓' : 'Not linked', color: '#2C5E3B', done: !!selectedProjectId },
              { label: 'Archive', sublabel: selectedArchiveId ? 'Linked ✓' : 'Not linked', color: '#C17A3E', done: !!selectedArchiveId },
              { label: 'The Wall', sublabel: selectedArchiveId ? 'Eligible after review' : 'Link archive first', color: '#1D4ED8', done: false },
            ].map((step, idx, arr) => (
              <React.Fragment key={step.label}>
                <div
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium"
                  style={{
                    backgroundColor: step.done ? `${step.color}10` : 'var(--muted)',
                    borderColor: step.done ? `${step.color}40` : 'var(--border)',
                    color: step.done ? step.color : 'var(--muted-foreground)',
                  }}
                >
                  <span>{step.label}</span>
                  <span className="opacity-70">{step.sublabel}</span>
                </div>
                {idx < arr.length - 1 && (
                  <ChevronRight size={12} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div
          className="rounded-xl border p-4 text-left text-sm mb-8"
          style={{ borderColor: '#D4A84744', backgroundColor: '#D4A84711' }}
        >
          <p className="font-medium mb-1" style={{ color: '#E8C86A' }}>
            What happens next
          </p>
          <ul className="space-y-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <li>• Auto-triage will scan for duplicate entries and flag sourced claims without citations</li>
            <li>• A curator (or historian) will review the full text and any attached citations</li>
            <li>• If approved, it will be published with your chosen certainty label visible to all readers</li>
            <li>• If rejected, you&apos;ll receive a reason and can revise and resubmit</li>
          </ul>
        </div>
        <a
          href="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-150 active:scale-95"
          style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
        >
          Back to Archive
        </a>
      </div>
    );
  }

  // Edit mode: loading state
  if (editLoading) {
    return (
      <div className="max-w-2xl mx-auto flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Loading story…</p>
        </div>
      </div>
    );
  }

  // Edit mode: load error
  if (editLoadError) {
    return (
      <div className="max-w-2xl mx-auto text-center py-24">
        <AlertTriangle size={32} className="mx-auto mb-4" style={{ color: '#E07070' }} />
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>Could not load story</h2>
        <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>{editLoadError}</p>
        <a
          href="/my-stories"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm"
          style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
        >
          My Stories
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-w-3xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1.5" style={{ color: 'var(--foreground)' }}>
          {isEditMode ? 'Edit Story' : 'Submit a Contribution'}
        </h1>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          {isEditMode
            ? 'Update your story details below. Changes are saved immediately and logged in the edit history.' :'Share historical knowledge with the archive. All submissions are reviewed by a human curator before publication. Certainty labels are mandatory and shown publicly.'}
        </p>
      </div>

      {/* Workflow Chain Banner */}
      <WorkflowChainBanner
        projectSelected={!!selectedProjectId}
        archiveSelected={!!selectedArchiveId && !isNewArchive}
      />

      {/* Submission Type Selector */}
      <div
        className="rounded-xl border p-5 mb-6"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <h2 className="text-sm font-semibold mb-1" style={{ color: 'var(--foreground)' }}>
          Submission Type
        </h2>
        <p className="text-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>
          Choose how you want to share this story. You can always add or remove audio later.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Text Only */}
          <button
            type="button"
            onClick={() => {
              setSubmissionType('text');
              if (!isFromRecording) {
                handleRemoveAudio();
              }
            }}
            className="relative rounded-xl border-2 p-4 text-left transition-all duration-200 group"
            style={{
              borderColor: submissionType === 'text' ? 'var(--primary)' : 'var(--border)',
              backgroundColor: submissionType === 'text' ? '#E05A4710' : 'var(--muted)',
            }}
          >
            {submissionType === 'text' && (
              <span
                className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            )}
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
              style={{
                backgroundColor: submissionType === 'text' ? '#E05A4720' : 'var(--card)',
              }}
            >
              <FileText
                size={18}
                style={{ color: submissionType === 'text' ? 'var(--primary)' : 'var(--muted-foreground)' }}
              />
            </div>
            <p
              className="text-sm font-semibold mb-1"
              style={{ color: submissionType === 'text' ? 'var(--primary)' : 'var(--foreground)' }}
            >
              Text Only
            </p>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              Write your historical account as a text entry. Ideal for documented records, oral traditions transcribed to text, or sourced claims.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {['Written account', 'Transcribed oral', 'Sourced claim'].map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: submissionType === 'text' ? '#E05A4718' : 'var(--card)',
                    color: submissionType === 'text' ? 'var(--primary)' : 'var(--muted-foreground)',
                    border: `1px solid ${submissionType === 'text' ? '#E05A4740' : 'var(--border)'}`,
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </button>

          {/* Audio + Text */}
          <button
            type="button"
            onClick={() => setSubmissionType('audio_text')}
            className="relative rounded-xl border-2 p-4 text-left transition-all duration-200 group"
            style={{
              borderColor: submissionType === 'audio_text' ? '#2C5E3B' : 'var(--border)',
              backgroundColor: submissionType === 'audio_text' ? '#2C5E3B10' : 'var(--muted)',
            }}
          >
            {submissionType === 'audio_text' && (
              <span
                className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#2C5E3B' }}
              >
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            )}
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
              style={{
                backgroundColor: submissionType === 'audio_text' ? '#2C5E3B22' : 'var(--card)',
              }}
            >
              <Mic
                size={18}
                style={{ color: submissionType === 'audio_text' ? '#2C5E3B' : 'var(--muted-foreground)' }}
              />
            </div>
            <div className="flex items-center gap-2 mb-1">
              <p
                className="text-sm font-semibold"
                style={{ color: submissionType === 'audio_text' ? '#2C5E3B' : 'var(--foreground)' }}
              >
                Audio + Text
              </p>
              {isFromRecording && audioUrl && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-1"
                  style={{ backgroundColor: '#2C5E3B', color: '#fff', fontSize: 10 }}
                >
                  <span style={{ fontSize: 8 }}>●</span> Recorded
                </span>
              )}
            </div>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              Attach an audio recording alongside your written account. Best for oral traditions, firsthand testimonies, and stories told in the speaker's own voice.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {['Oral tradition', 'Firsthand voice', 'Audio testimony'].map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: submissionType === 'audio_text' ? '#2C5E3B18' : 'var(--card)',
                    color: submissionType === 'audio_text' ? '#2C5E3B' : 'var(--muted-foreground)',
                    border: `1px solid ${submissionType === 'audio_text' ? '#2C5E3B40' : 'var(--border)'}`,
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </button>
        </div>

        {/* Difference explainer strip */}
        <div
          className="mt-4 rounded-lg px-4 py-3 flex items-start gap-3 text-xs"
          style={{ backgroundColor: 'var(--muted)', borderLeft: `3px solid ${submissionType === 'audio_text' ? '#2C5E3B' : 'var(--primary)'}` }}
        >
          <Info size={13} className="flex-shrink-0 mt-0.5" style={{ color: submissionType === 'audio_text' ? '#2C5E3B' : 'var(--primary)' }} />
          {submissionType === 'text' ? (
            <span style={{ color: 'var(--muted-foreground)' }}>
              <strong style={{ color: 'var(--foreground)' }}>Text Only</strong> — Your written account is the primary record. You can still add a featured image. Audio is not included.
            </span>
          ) : (
            <span style={{ color: 'var(--muted-foreground)' }}>
              <strong style={{ color: '#2C5E3B' }}>Audio + Text</strong> — Your voice recording is preserved alongside the written account. Readers can listen to the original telling and read the transcription.
            </span>
          )}
        </div>
      </div>

      {/* Draft Recovery Banner */}
      {!draftLoading && showDraftBanner && recoveredDraft && (
        <div
          className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 rounded-xl border mb-6"
          style={{ backgroundColor: '#D4A84711', borderColor: '#D4A84744' }}
        >
          <div className="flex items-start gap-2 flex-1">
            <RefreshCw size={15} className="shrink-0 mt-0.5" style={{ color: '#E8C86A' }} />
            <div>
              <p className="text-sm font-medium" style={{ color: '#E8C86A' }}>
                Unsaved draft found
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                Last saved{' '}
                {new Date(recoveredDraft.lastSavedAt).toLocaleString('en-GB', {
                  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                })}
                . Restore it to continue where you left off.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleRestoreDraft}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 active:scale-95"
              style={{ backgroundColor: '#D4A847', color: '#1a1a1a' }}
            >
              Restore draft
            </button>
            <button
              type="button"
              onClick={handleDiscardDraft}
              className="p-1.5 rounded-lg transition-colors duration-150"
              style={{ color: 'var(--muted-foreground)' }}
              aria-label="Discard draft"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {draftRestored && (
        <div
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border mb-6 text-xs"
          style={{ backgroundColor: '#2C5E3B11', borderColor: '#2C5E3B44', color: '#2C5E3B' }}
        >
          <RefreshCw size={13} />
          Draft restored — your previous work has been loaded.
        </div>
      )}

      {/* Auto-save indicator */}
      <div className="flex items-center gap-2 mb-6">
        {savingDraft ? (
          <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <div
              className="w-3 h-3 border border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--muted-foreground)' }}
            />
            Saving draft…
          </span>
        ) : draftSaved ? (
          <span className="flex items-center gap-1.5 text-xs" style={{ color: '#2C5E3B' }}>
            <Save size={12} />
            Draft saved
          </span>
        ) : (
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            Draft auto-saves every 5 seconds
          </span>
        )}
        <button
          type="button"
          onClick={saveDraft}
          className="text-xs px-2 py-1 rounded border transition-colors duration-150"
          style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
        >
          Save now
        </button>
      </div>

      {/* Section 1: Basic Info */}
      <div
        className="rounded-xl border p-6 mb-5"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <h2 className="text-base font-semibold mb-5" style={{ color: 'var(--foreground)' }}>
          Entry Details
        </h2>

        <div className="space-y-5">
          {/* Story Language */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
              {t('submission.language')} <span style={{ color: 'var(--status-rejected)' }}>*</span>
            </label>
            <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
              {t('submission.languageHint')}
            </p>
            <div className="flex flex-wrap gap-2">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setStoryLanguage(lang.code)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-150"
                  style={{
                    backgroundColor: storyLanguage === lang.code ? '#E05A4710' : 'var(--muted)',
                    borderColor: storyLanguage === lang.code ? 'var(--primary)' : 'var(--border)',
                    color: storyLanguage === lang.code ? 'var(--primary)' : 'var(--foreground)',
                    fontWeight: storyLanguage === lang.code ? 600 : 400,
                  }}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.nativeLabel}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
              Title <span style={{ color: 'var(--status-rejected)' }}>*</span>
            </label>
            <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
              A clear, descriptive title for the historical event, person, or knowledge you are documenting.
            </p>
            <input
              type="text"
              {...register('title', { required: 'Title is required', minLength: { value: 10, message: 'At least 10 characters' } })}
              placeholder="e.g. The Role of Spirit Mediums in the First Chimurenga"
              className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150"
              style={{
                backgroundColor: 'var(--input)',
                borderColor: errors.title ? 'var(--status-rejected)' : 'var(--border)',
                color: 'var(--foreground)',
              }}
              onFocus={(e) => {
                if (!errors.title) (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--primary)';
              }}
              onBlur={(e) => {
                if (!errors.title) (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)';
              }}
            />
            {errors.title && (
              <p className="text-xs mt-1" style={{ color: 'var(--status-rejected)' }}>
                {errors.title.message}
              </p>
            )}
          </div>

          {/* Era + Topic */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                Historical Era <span style={{ color: 'var(--status-rejected)' }}>*</span>
              </label>
              <select
                {...register('era', { required: 'Select an era' })}
                className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150 appearance-none"
                style={{
                  backgroundColor: 'var(--input)',
                  borderColor: errors.era ? 'var(--status-rejected)' : 'var(--border)',
                  color: 'var(--foreground)',
                }}
                onChange={(e) => {
                  setValue('era', e.target.value);
                  setValue('topic', '');
                  setIsCustomTopic(false);
                  setCustomTopicLabel('');
                }}
              >
                <option value="">Select era…</option>
                {eras.map((era) => (
                  <option key={era.id} value={era.id} style={{ backgroundColor: 'var(--card)' }}>
                    {era.label}
                  </option>
                ))}
              </select>
              {errors.era && (
                <p className="text-xs mt-1" style={{ color: 'var(--status-rejected)' }}>
                  {errors.era.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                Topic <span style={{ color: 'var(--status-rejected)' }}>*</span>
              </label>
              <select
                {...register('topic', {
                  required: isCustomTopic ? false : 'Select a topic',
                  validate: (val) => {
                    if (isCustomTopic) return true;
                    return val ? true : 'Select a topic';
                  },
                })}
                disabled={!selectedEra}
                className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150 appearance-none disabled:opacity-50"
                style={{
                  backgroundColor: 'var(--input)',
                  borderColor: errors.topic ? 'var(--status-rejected)' : 'var(--border)',
                  color: 'var(--foreground)',
                }}
                onChange={(e) => {
                  if (e.target.value === '__custom__') {
                    setIsCustomTopic(true);
                    setValue('topic', '__custom__');
                  } else {
                    setIsCustomTopic(false);
                    setCustomTopicLabel('');
                    setValue('topic', e.target.value);
                  }
                }}
              >
                <option value="">{selectedEra ? 'Select topic…' : 'Select era first'}</option>
                {availableTopics.map((topic) => (
                  <option key={topic.id} value={topic.id} style={{ backgroundColor: 'var(--card)' }}>
                    {topic.label}
                  </option>
                ))}
                {selectedEra && (
                  <option value="__custom__" style={{ backgroundColor: 'var(--card)', fontStyle: 'italic' }}>
                    + Add new topic…
                  </option>
                )}
              </select>
              {errors.topic && !isCustomTopic && (
                <p className="text-xs mt-1" style={{ color: 'var(--status-rejected)' }}>
                  {errors.topic.message}
                </p>
              )}

              {/* Custom topic input */}
              {isCustomTopic && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={customTopicLabel}
                      onChange={(e) => setCustomTopicLabel(e.target.value)}
                      placeholder="Enter a new topic name…"
                      className="flex-1 px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150"
                      style={{
                        backgroundColor: 'var(--input)',
                        borderColor: isCustomTopic && !customTopicLabel.trim() ? 'var(--status-rejected)' : 'var(--primary)',
                        color: 'var(--foreground)',
                      }}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setIsCustomTopic(false);
                        setCustomTopicLabel('');
                        setValue('topic', '');
                      }}
                      className="p-2 rounded-lg border transition-colors duration-150"
                      style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
                      aria-label="Cancel custom topic"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {isCustomTopic && !customTopicLabel.trim() && (
                    <p className="text-xs mt-1" style={{ color: 'var(--status-rejected)' }}>
                      Please enter a topic name.
                    </p>
                  )}
                  <p className="text-xs mt-1.5" style={{ color: 'var(--muted-foreground)' }}>
                    Your new topic will be submitted for review alongside your contribution.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Region */}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
              Region
            </label>
            <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
              Geographic area this contribution primarily relates to.
            </p>
            <select
              {...register('region')}
              className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150 appearance-none"
              style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
            >
              <option value="">Select region (optional)</option>
              {regions.map((r) => (
                <option key={`region-${r}`} value={r} style={{ backgroundColor: 'var(--card)' }}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Section 2: Certainty Label */}
      <div
        className="rounded-xl border p-6 mb-5"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <h2 className="text-base font-semibold mb-2" style={{ color: 'var(--foreground)' }}>
          Certainty Label <span style={{ color: 'var(--status-rejected)' }}>*</span>
        </h2>
        <p className="text-xs mb-5" style={{ color: 'var(--muted-foreground)' }}>
          This label is shown publicly on every contribution. It tells readers how to interpret the knowledge — we never imply more certainty than the source warrants.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {certaintyOptions.map((opt) => {
            const isSelected = certaintyLabel === opt.id;
            return (
              <label
                key={`certainty-${opt.id}`}
                className={`cursor-pointer rounded-xl border p-4 transition-all duration-150 ${isSelected ? 'ochre-glow' : ''}`}
                style={{
                  borderColor: isSelected ? 'var(--primary)' : 'var(--border)',
                  backgroundColor: isSelected ? '#E05A4710' : 'var(--muted)',
                }}
              >
                <input
                  type="radio"
                  value={opt.id}
                  {...register('certaintyLabel', { required: 'Select a certainty label' })}
                  className="sr-only"
                />
                <div className="flex items-center gap-2 mb-2">
                  <opt.icon size={16} style={{ color: isSelected ? 'var(--primary)' : 'var(--muted-foreground)' }} />
                  <Badge variant={opt.badge} label={opt.label} />
                </div>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  {opt.desc}
                </p>
              </label>
            );
          })}
        </div>
        {errors.certaintyLabel && (
          <p className="text-xs mt-3" style={{ color: 'var(--status-rejected)' }}>
            {errors.certaintyLabel.message}
          </p>
        )}

        {certaintyLabel === 'sourced_claim' && citations.length === 0 && (
          <div
            className="flex items-start gap-2 mt-4 px-4 py-3 rounded-lg border text-xs"
            style={{ backgroundColor: '#D4A84711', borderColor: '#D4A84744', color: '#E8C86A' }}
          >
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            Sourced Claim requires at least one citation. Add one below.
          </div>
        )}
      </div>

      {/* Section 3: Body */}
      <div
        className="rounded-xl border p-6 mb-5"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
            Content <span style={{ color: 'var(--status-rejected)' }}>*</span>
          </h2>
          <span
            className="text-xs tabular-nums font-mono"
            style={{
              color: wordCount < 50 ? 'var(--muted-foreground)' : wordCount > 5000 ? 'var(--status-rejected)' : '#2C5E3B',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {wordCount} words
          </span>
        </div>
        <p className="text-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>
          Write the full historical account. Minimum 50 words. Use headings, quotes, and lists to structure long entries.
        </p>

        <div
          className="rounded-lg border overflow-hidden"
          style={{ borderColor: 'var(--border)' }}
        >
          <RichTextEditor
            onChange={handleBodyChange}
            placeholder="Begin your historical account here…"
            initialContent={isEditMode ? editInitialBody : voiceInitialContent}
          />
        </div>

        {wordCount < 50 && wordCount > 0 && (
          <p className="text-xs mt-2" style={{ color: 'var(--status-rejected)' }}>
            Minimum 50 words required ({50 - wordCount} more needed)
          </p>
        )}
      </div>

      {/* Section 3c: Featured Image */}
      <div
        className="rounded-xl border p-6 mb-5"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Image size={16} style={{ color: 'var(--primary)' }} />
          <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>Featured Image</h2>
          <span className="text-xs px-2 py-0.5 rounded-full ml-1" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>Optional</span>
        </div>
        <p className="text-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>
          Upload a representative image that will appear as the cover for this story.
        </p>

        {featuredImagePreview ? (
          <div className="relative rounded-xl overflow-hidden mb-3" style={{ maxHeight: 240 }}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={featuredImagePreview} alt="Featured image preview" className="w-full object-cover" style={{ maxHeight: 240 }} />
            <button
              type="button"
              onClick={() => { setFeaturedImageUrl(''); setFeaturedImagePreview(''); }}
              className="absolute top-2 right-2 p-1.5 rounded-full"
              style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff' }}
              aria-label="Remove featured image"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div
            className="rounded-xl border-2 border-dashed flex flex-col items-center justify-center py-10 mb-3 cursor-pointer transition-colors"
            style={{ borderColor: 'var(--border)' }}
            onClick={() => featuredImageRef.current?.click()}
          >
            <Image size={28} className="mb-2" style={{ color: 'var(--muted-foreground)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
              {featuredImageUploading ? 'Uploading…' : 'Click to upload featured image'}
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>JPG, PNG, WebP up to 50MB</p>
          </div>
        )}

        <input
          ref={featuredImageRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFeaturedImageUpload}
        />

        {!featuredImagePreview && (
          <div className="flex gap-2">
            <input
              type="url"
              value={featuredImageUrl}
              onChange={e => { setFeaturedImageUrl(e.target.value); setFeaturedImagePreview(e.target.value); }}
              placeholder="Or paste an image URL…"
              className="flex-1 px-3 py-2 rounded-lg border text-sm outline-none"
              style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
            />
          </div>
        )}
      </div>

      {/* Section 3d: Audio Submission — only shown when audio_text type selected */}
      {submissionType === 'audio_text' && (
      <div
        className="rounded-xl border p-6 mb-5"
        style={{
          backgroundColor: 'var(--card)',
          borderColor: isFromRecording && audioUrl ? '#2C5E3B' : '#2C5E3B66',
          boxShadow: isFromRecording && audioUrl ? '0 0 0 2px #2C5E3B33' : undefined,
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Music size={16} style={{ color: '#2C5E3B' }} />
          <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>Audio Recording</h2>
          {isFromRecording && audioUrl ? (
            <span
              className="text-xs px-2 py-0.5 rounded-full ml-1 font-semibold flex items-center gap-1"
              style={{ backgroundColor: '#2C5E3B', color: '#fff' }}
            >
              <span style={{ fontSize: 9 }}>●</span> Ready to Submit
            </span>
          ) : (
            <span
              className="text-xs px-2 py-0.5 rounded-full ml-1 font-semibold"
              style={{ backgroundColor: '#2C5E3B18', color: '#2C5E3B', border: '1px solid #2C5E3B44' }}
            >
              Required for this type
            </span>
          )}
        </div>

        {/* Banner: audio from recording is ready */}
        {isFromRecording && audioUrl && (
          <div
            className="rounded-lg px-4 py-3 mb-4 flex items-start gap-3"
            style={{ backgroundColor: '#2C5E3B15', border: '1px solid #2C5E3B44' }}
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: '#2C5E3B22' }}
            >
              <Mic size={15} style={{ color: '#2C5E3B' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" style={{ color: '#2C5E3B' }}>
                Your recorded audio is attached and ready
              </p>
              <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                The audio you recorded has been added to this submission. Fill in the story details above and click <strong>Submit Contribution</strong> to publish it.
              </p>
            </div>
          </div>
        )}

        <p className="text-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>
          Upload an audio recording of this story. Supported formats: MP3, MP4, OGG, WAV, M4A, WebM (max 100 MB).
        </p>

        {audioUrl && !audioUploading ? (
          <div
            className="rounded-xl border p-4 mb-3 flex items-center gap-3"
            style={{ borderColor: '#2C5E3B44', backgroundColor: '#2C5E3B0A' }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#2C5E3B22' }}
            >
              <Music size={16} style={{ color: '#2C5E3B' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: 'var(--foreground)' }}>
                {audioUploadFile?.name ?? 'Audio recording attached'}
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                {audioFileSizeBytes ? `${(audioFileSizeBytes / (1024 * 1024)).toFixed(1)} MB` : ''}
                {audioFileSizeBytes && audioDurationSeconds ? ' · ' : ''}
                {audioDurationSeconds ? `${Math.floor(audioDurationSeconds / 60)}:${String(audioDurationSeconds % 60).padStart(2, '0')}` : ''}
              </p>
              <audio controls src={audioUrl} className="mt-2 w-full" style={{ height: 32 }} />
            </div>
            <button
              type="button"
              onClick={handleRemoveAudio}
              className="p-1.5 rounded-full flex-shrink-0 transition-colors"
              style={{ color: 'var(--muted-foreground)' }}
              aria-label="Remove audio"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div
            className="rounded-xl border-2 border-dashed flex flex-col items-center justify-center py-10 mb-3 cursor-pointer transition-colors"
            style={{ borderColor: audioUploading ? '#2C5E3B' : '#2C5E3B66' }}
            onClick={() => !audioUploading && audioUploadRef.current?.click()}
          >
            {audioUploading ? (
              <>
                <div
                  className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mb-3"
                  style={{ borderColor: '#2C5E3B' }}
                />
                <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>Uploading audio…</p>
              </>
            ) : (
              <>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                  style={{ backgroundColor: '#2C5E3B18' }}
                >
                  <Mic size={22} style={{ color: '#2C5E3B' }} />
                </div>
                <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>Click to upload audio recording</p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>MP3, MP4, OGG, WAV, M4A, WebM up to 100 MB</p>
              </>
            )}
          </div>
        )}

        <input
          ref={audioUploadRef}
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={handleAudioUpload}
        />

        {audioUploadError && (
          <p className="text-xs mt-1" style={{ color: 'var(--status-rejected)' }}>
            {audioUploadError}
          </p>
        )}
      </div>
      )}

      {/* Section 3b: AI Citation Authenticator */}
      <div className="mb-5">
        <CitationAuthenticator
          title={watchedTitle || ''}
          body={body}
          era={selectedEra || ''}
          topic={watch('topic') || ''}
          onApplyCitation={handleApplySuggestedCitation}
        />
      </div>

      {/* Section 4: Citations */}
      <div
        className="rounded-xl border mb-5 overflow-hidden"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <button
          type="button"
          onClick={() => setCitationsExpanded(!citationsExpanded)}
          className="w-full flex items-center justify-between px-6 py-4 transition-colors duration-150"
          style={{ color: 'var(--foreground)' }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--muted)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
          }}
        >
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">Citations</h2>
            {citations.length > 0 && (
              <span
                className="px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}
              >
                {citations.length}
              </span>
            )}
            {certaintyLabel === 'sourced_claim' && (
              <span className="text-xs" style={{ color: 'var(--status-rejected)' }}>
                (required for Sourced Claim)
              </span>
            )}
          </div>
          {citationsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {citationsExpanded && (
          <div className="px-6 pb-6">
            <p className="text-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>
              Add sources that support this contribution. Required for Sourced Claim entries.
            </p>

            {citations.length === 0 ? (
              <div
                className="rounded-lg border border-dashed flex flex-col items-center justify-center py-8 mb-4"
                style={{ borderColor: 'var(--border)' }}
              >
                <BookOpen size={24} style={{ color: 'var(--muted-foreground)' }} className="mb-2" />
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  No citations added yet
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                  Add archive documents, books, or web sources to support your entry
                </p>
              </div>
            ) : (
              <div className="space-y-4 mb-4">
                {citations.map((citation, idx) => (
                  <div
                    key={citation.id}
                    className="rounded-lg border p-4"
                    style={{ borderColor: 'var(--border)', backgroundColor: 'var(--muted)' }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>
                        SOURCE {idx + 1}
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor:
                              citation.verificationStatus === 'verified'
                                ? '#5B9E6B22'
                                : citation.verificationStatus === 'broken' ? '#E05A4722' : '#F39C1222',
                            color:
                              citation.verificationStatus === 'verified'
                                ? '#2C5E3B'
                                : citation.verificationStatus === 'broken' ? '#B03020' : '#A06800',
                          }}
                        >
                          {citation.verificationStatus}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeCitation(citation.id)}
                          className="p-1 rounded transition-colors duration-150"
                          style={{ color: 'var(--muted-foreground)' }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.color = 'var(--status-rejected)';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted-foreground)';
                          }}
                          aria-label="Remove citation"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--foreground)' }}>
                          Title
                        </label>
                        <input
                          type="text"
                          value={citation.title}
                          onChange={(e) => updateCitation(citation.id, 'title', e.target.value)}
                          placeholder="Source title"
                          className="w-full px-3 py-2 rounded-lg border text-xs outline-none transition-colors duration-150"
                          style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                          onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--primary)'; }}
                          onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)'; }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--foreground)' }}>
                          Author
                        </label>
                        <input
                          type="text"
                          value={citation.author}
                          onChange={(e) => updateCitation(citation.id, 'author', e.target.value)}
                          placeholder="Author name"
                          className="w-full px-3 py-2 rounded-lg border text-xs outline-none transition-colors duration-150"
                          style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                          onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--primary)'; }}
                          onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)'; }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--foreground)' }}>
                          Publication Date
                        </label>
                        <input
                          type="text"
                          value={citation.publicationDate}
                          onChange={(e) => updateCitation(citation.id, 'publicationDate', e.target.value)}
                          placeholder="e.g. 1978 or 15 Mar 1978"
                          className="w-full px-3 py-2 rounded-lg border text-xs outline-none transition-colors duration-150"
                          style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                          onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--primary)'; }}
                          onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)'; }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--foreground)' }}>
                          Document Type
                        </label>
                        <select
                          value={citation.documentType}
                          onChange={(e) => updateCitation(citation.id, 'documentType', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border text-xs outline-none appearance-none"
                          style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                        >
                          {documentTypes.map((dt) => (
                            <option key={`dt-${dt}`} value={dt} style={{ backgroundColor: 'var(--card)' }}>
                              {dt}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--foreground)' }}>
                          URL
                        </label>
                        <input
                          type="url"
                          value={citation.url}
                          onChange={(e) => updateCitation(citation.id, 'url', e.target.value)}
                          placeholder="https://…"
                          className="w-full px-3 py-2 rounded-lg border text-xs outline-none transition-colors duration-150"
                          style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                          onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--primary)'; }}
                          onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)'; }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--foreground)' }}>
                          Archive Source
                        </label>
                        <input
                          type="text"
                          value={citation.archiveSource}
                          onChange={(e) => updateCitation(citation.id, 'archiveSource', e.target.value)}
                          placeholder="e.g. NAZ/S235/476"
                          className="w-full px-3 py-2 rounded-lg border text-xs outline-none transition-colors duration-150"
                          style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                          onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--primary)'; }}
                          onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)'; }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={addCitation}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-150 active:scale-95"
              style={{ borderColor: 'var(--border)', color: 'var(--foreground)', backgroundColor: 'var(--muted)' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = '#E05A4744';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)';
              }}
            >
              <Plus size={14} />
              Add Citation
            </button>
          </div>
        )}
      </div>

      {/* Section 4b: Archive Selection */}
      <div className="mb-5">
        <ArchiveSelector
          title={watchedTitle || ''}
          body={body}
          era={selectedEra || ''}
          topic={watch('topic') || ''}
          region={watch('region') || ''}
          onArchiveSelected={handleArchiveSelected}
        />
      </div>

      {/* Section 4c: Link to Project */}
      <div
        className="rounded-xl border p-5 mb-5"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-2 mb-2">
          <FolderOpen size={16} style={{ color: '#2C5E3B' }} />
          <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
            Link to a Project
          </h2>
          <span
            className="text-xs px-2 py-0.5 rounded-full ml-1"
            style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}
          >
            Step 2 of workflow
          </span>
        </div>
        <p className="text-xs mb-3" style={{ color: 'var(--muted-foreground)' }}>
          Stories linked to a project are easier to track and contribute to an archive. Projects connect stories to archives, which connect to The Wall.
        </p>

        {loadingProjects ? (
          <div className="flex items-center gap-2 text-xs py-2" style={{ color: 'var(--muted-foreground)' }}>
            <div className="w-3 h-3 border border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--muted-foreground)' }} />
            Loading your projects…
          </div>
        ) : userProjects.length > 0 ? (
          <>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150 appearance-none"
              style={{ backgroundColor: 'var(--input)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
            >
              <option value="">No project (standalone story)</option>
              {userProjects.map((p) => (
                <option key={p.id} value={p.id} style={{ backgroundColor: 'var(--card)' }}>
                  {p.title} ({p.category})
                </option>
              ))}
            </select>
            {selectedProjectId && (
              <p className="text-xs mt-2 flex items-center gap-1" style={{ color: '#2C5E3B' }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6L4.5 8.5L10 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Story will be linked to this project after submission
              </p>
            )}
          </>
        ) : (
          <div
            className="flex items-start gap-3 p-3 rounded-lg border"
            style={{ backgroundColor: '#2C5E3B08', borderColor: '#2C5E3B30' }}
          >
            <FolderOpen size={16} className="shrink-0 mt-0.5" style={{ color: '#2C5E3B' }} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium mb-1" style={{ color: 'var(--foreground)' }}>
                No active projects yet
              </p>
              <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                Create a documentation project to organise your stories and connect them to archives and The Wall.
              </p>
              <a
                href="/my-projects"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all active:scale-95"
                style={{ backgroundColor: '#2C5E3B', color: '#fff' }}
              >
                <Plus size={12} />
                Create a Project
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Section 4d: Media Uploads */}
      <div
        className="rounded-xl border p-6 mb-5"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Video size={16} style={{ color: 'var(--primary)' }} />
          <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>Media Attachments</h2>
          <span className="text-xs px-2 py-0.5 rounded-full ml-1" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>Optional</span>
        </div>
        <p className="text-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>
          Attach images, audio recordings, or videos that support this contribution. Media is linked after submission.
        </p>
        <div className="rounded-lg border border-dashed flex flex-col items-center justify-center py-8 gap-2"
          style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3">
            <Image size={20} style={{ color: 'var(--muted-foreground)' }} />
            <Video size={20} style={{ color: 'var(--muted-foreground)' }} />
          </div>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            Media uploads are available after submission
          </p>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            Once submitted, you can attach images, audio, and video from your contribution page
          </p>
        </div>
      </div>

      {/* Section 5: Submission Options */}
      <div
        className="rounded-xl border p-6 mb-8"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <h2 className="text-base font-semibold mb-4" style={{ color: 'var(--foreground)' }}>
          Submission Options
        </h2>

        <div
          className="flex items-start gap-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--muted)' }}
        >
          <div className="flex-1">
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--foreground)' }}>
              Submit anonymously
            </p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Your username will not appear on the published entry. Your account is still linked internally for
              moderation purposes and future reputation tracking.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isAnonymous}
            onClick={() => setValue('isAnonymous', !isAnonymous)}
            className="flex-shrink-0 mt-0.5 relative w-10 h-5 rounded-full transition-colors duration-200"
            style={{ backgroundColor: isAnonymous ? 'var(--primary)' : 'var(--border)' }}
          >
            <span
              className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
              style={{ transform: isAnonymous ? 'translateX(20px)' : 'translateX(2px)' }}
            />
          </button>
        </div>

        <div
          className="flex items-start gap-2 mt-4 px-4 py-3 rounded-lg border text-xs"
          style={{ backgroundColor: '#00808011', borderColor: '#00808044', color: '#006060' }}
        >
          <Info size={14} className="flex-shrink-0 mt-0.5" />
          All submissions start as <strong className="mx-1">pending</strong> and require curator approval before
          publication. Auto-triage adds flags only — it never changes your submission&apos;s status.
        </div>
      </div>

      {/* Rate limit / submit error */}
      {submitError && (
        <div
          className="flex items-start gap-2 px-4 py-3 rounded-xl border text-sm mb-4"
          style={{ backgroundColor: '#E0707011', borderColor: '#E0707044', color: '#E07070' }}
        >
          <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          {submitError}
        </div>
      )}

      {/* Submit bar */}
      <div
        className="sticky bottom-0 flex items-center justify-between p-4 rounded-xl border"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          <span className="font-medium" style={{ color: 'var(--foreground)' }}>
            {wordCount}
          </span>{' '}
          words · {citations.length} citation{citations.length !== 1 ? 's' : ''}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={saveDraft}
            className="px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-150 active:scale-95"
            style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
          >
            Save Draft
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95 disabled:opacity-60"
            style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            {submitting ? (
              <>
                <div
                  className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                  style={{ borderColor: 'var(--primary-foreground)' }}
                />
                Submitting…
              </>
            ) : (
              <>
                <Send size={14} />
                Submit for Review
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}